import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api-client';
import { logger } from '@/lib/default-logger';
import { toast } from '@/components/core/toaster';
import { usersKeys } from './use-users';

export interface AccountUser {
  id: string;
  name: string;
  email: string;
  status: "active" | "inactive";
  user: {
    id: string;
    email: string;
  };
  role: {
    name: string;
    title: string;
  };
}

export const accountUsersKeys = {
  all: ['account-users'] as const,
  lists: () => [...accountUsersKeys.all, 'list'] as const,
  list: (accountId: string, query?: string) => [...accountUsersKeys.lists(), accountId, { query }] as const,
  details: () => [...accountUsersKeys.all, 'detail'] as const,
  detail: (accountId: string, userId: string) => [...accountUsersKeys.details(), accountId, userId] as const,
};

export function useAccountUsersList(accountId: string, query?: string) {
  return useQuery({
    queryKey: accountUsersKeys.list(accountId, query),
    queryFn: async () => {
      const queryString = query ? `?${query}` : '';
      const response = await apiClient.get(`/accounts/${accountId}/users${queryString}`);
      return response.data;
    },
    enabled: !!accountId,
  });
}

export function useAccountUser(accountId: string, userId: string) {
  return useQuery({
    queryKey: accountUsersKeys.detail(accountId, userId),
    queryFn: async () => {
      const response = await apiClient.get(`/accounts/${accountId}/users/${userId}`);
      return response.data;
    },
    enabled: !!accountId && !!userId,
  });
}

export function useCreateAccountUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ accountId, data }: { accountId: string; data: { role: string; userEmail: string } }) => {
      const response = await apiClient.post(`/accounts/${accountId}/users`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountUsersKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountUsersKeys.list(variables.accountId) });
      toast.success('User created successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'User creation failed');
    },
  });
}

export function useUpdateAccountUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ accountId, userId, data }: { accountId: string; userId: string; data: { role: string } }) => {
      const response = await apiClient.put(`/accounts/${accountId}/users/${userId}`, {
        accountId,
        ...data,
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      queryClient.setQueryData(accountUsersKeys.detail(variables.accountId, variables.userId), data);
      queryClient.invalidateQueries({ queryKey: accountUsersKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountUsersKeys.list(variables.accountId) });
      toast.success('User updated successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'User update failed');
    },
  });
}

export function useDeleteAccountUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ accountId, userId }: { accountId: string; userId: string }) => {
      const response = await apiClient.delete(`/accounts/${accountId}/users/${userId}`);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      queryClient.removeQueries({ queryKey: accountUsersKeys.detail(variables.accountId, variables.userId) });
      queryClient.invalidateQueries({ queryKey: accountUsersKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountUsersKeys.list(variables.accountId) });
      toast.success('User deleted successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'User deletion failed');
    },
  });
}
